import { useMemo, useState, useEffect } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, usePropertyField } from '@vibesites/runtime/react';
import { RichTextContent } from '@vibesites/runtime/react';
import { EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import * as Accordion from '@radix-ui/react-accordion';
import Confetti from 'react-confetti';

/**
 * Formats a raw bedrooms range (e.g. "studio - 2") into natural copy
 * like "Studio - 2 Bedroom". Handles single values ("1" → "1 Bedroom"),
 * ranges ("1 - 3" → "1 - 3 Bedroom"), and studios. Ranges where both
 * endpoints are the same are collapsed (e.g. "2 - 2" → "2 Bedroom").
 */
function formatBedroomsLabel(raw: string | null | undefined): string {
  if (!raw) return 'Apartments';
  const trimmed = raw.trim();
  if (!trimmed) return 'Apartments';

  const parts = trimmed.split(/\s*-\s*/);
  const formatted = parts.map((p) => {
    const lower = p.trim().toLowerCase();
    if (lower === 'studio' || lower === 'std') return 'Studio';
    return p.trim();
  });

  // Collapse ranges where every part is identical (e.g. "2 - 2" → "2").
  const deduped =
    formatted.length > 1 && formatted.every((p) => p === formatted[0])
      ? [formatted[0]]
      : formatted;

  const label = deduped.join(' - ');
  const allStudios = deduped.every((p) => p === 'Studio');
  return allStudios ? `${label}` : `${label} Bedroom`;
}

const featuredImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Property hero image',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: {
      image: 'image',
      alt: 'alt_text',
    },
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/floor-plans',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'View Availability',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

const galleryCtaDef: SlotGroupDefinition = {
  label: 'Gallery CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/gallery',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Show Gallery',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

const amenitiesCtaDef: SlotGroupDefinition = {
  label: 'Amenities CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/amenities',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Amenities',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

const petPolicyCtaDef: SlotGroupDefinition = {
  label: 'Pet Policy CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/pet-policy',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Pet Policy',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

const confettiCtaDef: SlotGroupDefinition = {
  label: 'Confetti CTA',
  fields: {
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Celebrate',
    },
  },
};

export function Hero() {
  const bgImage = useSlotGroup('hero.bg_image', featuredImageDef);
  const ctaLink = useSlotGroup('hero.cta_link', ctaLinkDef);
  const galleryCta = useSlotGroup('hero.gallery_cta', galleryCtaDef);
  const amenitiesCta = useSlotGroup('hero.amenities_cta', amenitiesCtaDef);
  const petPolicyCta = useSlotGroup('hero.pet_policy_cta', petPolicyCtaDef);
  const confettiCta = useSlotGroup('hero.confetti_cta', confettiCtaDef);

  const [showConfetti, setShowConfetti] = useState(false);
  const [windowSize, setWindowSize] = useState({ width: 0, height: 0 });

  // Track viewport size so the confetti canvas covers the full screen.
  // Runs client-side only, so SSR never touches `window`.
  useEffect(() => {
    const update = () =>
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const bedroomsRange = usePropertyField('property_bedrooms_range');
  const city = usePropertyField('property_address_city');
  const state = usePropertyField('property_address_state');

  const subtitleHtml = useMemo(() => {
    const beds = formatBedroomsLabel(bedroomsRange as string | null);
    const cityStr = city || '';
    const stateStr = state || '';
    const location = [cityStr, stateStr].filter(Boolean).join(', ');
    return `<p>${beds} Apartments for Rent${location ? ` in ${location}` : ''}</p>`;
  }, [bedroomsRange, city, state]);

  return (
    <section className="relative w-full h-[75vh] md:h-screen min-h-[400px] md:min-h-[600px] max-h-[1000px] overflow-hidden">
      {/* Confetti overlay — fixed to the viewport, mounts only after a click */}
      {showConfetti && (
        <Confetti
          width={windowSize.width}
          height={windowSize.height}
          recycle={false}
          numberOfPieces={450}
          onConfettiComplete={(confetti) => {
            setShowConfetti(false);
            confetti?.reset();
          }}
          style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 50 }}
        />
      )}

      {/* Ken Burns animated background */}
      <EditableSlotGroup groupPath="hero.bg_image" definition={featuredImageDef}>
        <div className="absolute inset-0">
          {bgImage.image ? (
            <VibesitesImage
              url={bgImage.image}
              alt={bgImage.alt || ''}
              {...{ loading: 'eager', fetchPriority: 'high', decoding: 'async' } as any}
              className="w-full h-full object-cover"
              style={{
                animation: 'heroKenBurns 30s ease-in-out infinite alternate',
                willChange: 'transform',
                transformOrigin: 'bottom right',
                imageRendering: 'high-quality' as any,
              }}
            />
          ) : (
            <div className="w-full h-full bg-[var(--vibe-color-text)]" />
          )}
        </div>
      </EditableSlotGroup>

      {/* Gradient overlay for text contrast */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/35 to-black/20" />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-6 sm:px-8">
        <RichTextContent
          path="hero.title"
          label="Title"
          defaultValue='<p>Welcome to <span data-type="field-token" data-field="property_name"></span></p>'
          as="h1"
          className="hero-entrance hero-entrance-1 font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-normal mb-5 max-w-5xl leading-[1.1] tracking-tight drop-shadow-lg"
        />

        <RichTextContent
          path="hero.subtitle"
          label="Subtitle"
          defaultValue={subtitleHtml}
          as="p"
          className="hero-entrance hero-entrance-2 font-body text-lg sm:text-xl md:text-2xl max-w-3xl mb-4 leading-relaxed opacity-90 drop-shadow-md"
        />

        <RichTextContent
          path="hero.romance_blurb"
          label="Home Romance Blurb"
          defaultValue="<p>Find your perfect home in a community built for modern life. Enjoy upscale finishes, resort-style amenities, and a vibrant neighborhood right outside your door. Come see why residents love calling this place home.</p>"
          as="p"
          className="hero-entrance hero-entrance-2 font-body text-sm sm:text-base md:text-lg max-w-2xl mb-10 leading-relaxed opacity-75 drop-shadow-md"
        />

        <div className="hero-entrance hero-entrance-3 flex flex-col sm:flex-row items-center gap-4">
          <EditableSlotGroup groupPath="hero.cta_link" definition={ctaLinkDef}>
            <a
              href={ctaLink.url || '/floor-plans'}
              target={ctaLink._target || '_self'}
              className="hero-cta group/cta relative inline-flex items-center gap-2.5 px-10 py-4 text-sm font-semibold tracking-[0.2em] uppercase text-[var(--vibe-color-text-on-primary)] overflow-hidden transition-all duration-300"
            >
              <span className="relative z-10">{ctaLink.label || 'View Availability'}</span>
              <svg className="relative z-10 w-4 h-4 transition-transform duration-300 group-hover/cta:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
              </svg>
            </a>
          </EditableSlotGroup>

          <EditableSlotGroup groupPath="hero.gallery_cta" definition={galleryCtaDef}>
            <a
              href={galleryCta.url || '/gallery'}
              target={galleryCta._target || '_self'}
              className="hero-cta-secondary group/gallery relative inline-flex items-center gap-2.5 px-10 py-4 text-sm font-semibold tracking-[0.2em] uppercase text-white overflow-hidden transition-all duration-300"
            >
              <span className="relative z-10">{galleryCta.label || 'Show Gallery'}</span>
              <svg className="relative z-10 w-4 h-4 transition-transform duration-300 group-hover/gallery:scale-110" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
              </svg>
            </a>
          </EditableSlotGroup>

          <EditableSlotGroup groupPath="hero.amenities_cta" definition={amenitiesCtaDef}>
            <a
              href={amenitiesCta.url || '/amenities'}
              target={amenitiesCta._target || '_self'}
              className="hero-cta-secondary group/amenities relative inline-flex items-center gap-2.5 px-10 py-4 text-sm font-semibold tracking-[0.2em] uppercase text-white overflow-hidden transition-all duration-300"
            >
              <span className="relative z-10">{amenitiesCta.label || 'Amenities'}</span>
              <svg className="relative z-10 w-4 h-4 transition-transform duration-300 group-hover/amenities:scale-110" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09Z" />
              </svg>
            </a>
          </EditableSlotGroup>

          <EditableSlotGroup groupPath="hero.pet_policy_cta" definition={petPolicyCtaDef}>
            <a
              href={petPolicyCta.url || '/pet-policy'}
              target={petPolicyCta._target || '_self'}
              className="hero-cta-secondary group/pet relative inline-flex items-center gap-2.5 px-10 py-4 text-sm font-semibold tracking-[0.2em] uppercase text-white overflow-hidden transition-all duration-300"
            >
              <span className="relative z-10">{petPolicyCta.label || 'Pet Policy'}</span>
              <svg className="relative z-10 w-4 h-4 transition-transform duration-300 group-hover/pet:scale-110" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 8.25a1.75 1.75 0 1 1-3.5 0 1.75 1.75 0 0 1 3.5 0Zm11 0a1.75 1.75 0 1 1-3.5 0 1.75 1.75 0 0 1 3.5 0Zm-8-3a1.75 1.75 0 1 1-3.5 0 1.75 1.75 0 0 1 3.5 0Zm5 0a1.75 1.75 0 1 1-3.5 0 1.75 1.75 0 0 1 3.5 0Zm-4.75 5.5c-2 0-4.5 2.5-4.5 5 0 1.5 1 2.5 2.5 2.5.75 0 1.25-.25 2-.25s1.25.25 2 .25c1.5 0 2.5-1 2.5-2.5 0-2.5-2.5-5-4.5-5Z" />
              </svg>
            </a>
          </EditableSlotGroup>

          <EditableSlotGroup groupPath="hero.confetti_cta" definition={confettiCtaDef}>
            <button
              type="button"
              onClick={() => setShowConfetti(true)}
              className="hero-cta-secondary group/confetti relative inline-flex items-center gap-2.5 px-10 py-4 text-sm font-semibold tracking-[0.2em] uppercase text-white overflow-hidden transition-all duration-300 cursor-pointer"
            >
              <span className="relative z-10">{confettiCta.label || 'Celebrate'}</span>
              <svg className="relative z-10 w-4 h-4 transition-transform duration-300 group-hover/confetti:scale-110" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09Z" />
              </svg>
            </button>
          </EditableSlotGroup>
        </div>

        {/* Learn More accordion — trigger sits in flow; content is absolutely
            positioned so it overlays downward without shifting anything above. */}
        <Accordion.Root type="single" collapsible className="hero-entrance hero-entrance-4 w-full max-w-2xl mt-6">
          <Accordion.Item value="learn-more" className="hero-accordion-item relative">
            <Accordion.Header>
              <Accordion.Trigger className="hero-accordion-trigger group/acc flex items-center justify-center gap-2 w-full py-3 text-sm font-medium tracking-[0.15em] uppercase text-white/80 transition-colors duration-200 hover:text-white cursor-pointer">
                <span>Learn More</span>
                <svg
                  className="w-4 h-4 transition-transform duration-300 group-data-[state=open]/acc:rotate-180"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </Accordion.Trigger>
            </Accordion.Header>
            <Accordion.Content className="hero-accordion-content absolute left-0 right-0 top-full z-20 overflow-hidden">
              <div className="hero-accordion-body rounded-md px-6 py-5 text-left text-white/90 text-sm sm:text-base leading-relaxed">
                <RichTextContent
                  path="hero.learn_more"
                  label="Learn More Content"
                  defaultValue='<p>Discover modern living at <span data-type="field-token" data-field="property_name"></span>. Our community offers thoughtfully designed floor plans, premium amenities, and an unbeatable location. Schedule a tour today to see your future home.</p>'
                  as="div"
                  className="hero-learn-more-content"
                />
              </div>
            </Accordion.Content>
          </Accordion.Item>
        </Accordion.Root>
      </div>

      <style>{`
        @keyframes heroKenBurns {
          0% { transform: scale(1.01) translate(0, 0); }
          100% { transform: scale(1.05) translate(-0.8%, -0.5%); }
        }
        @keyframes heroEntrance {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hero-entrance {
          animation: heroEntrance 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .hero-entrance-1 { animation-delay: 0.2s; }
        .hero-entrance-2 { animation-delay: 0.45s; }
        .hero-entrance-3 { animation-delay: 0.7s; }
        .hero-entrance-4 { animation-delay: 0.9s; }
        .hero-cta {
          background: linear-gradient(135deg, var(--vibe-color-primary) 0%, color-mix(in srgb, var(--vibe-color-primary) 80%, black) 100%);
          border-radius: 2px;
          box-shadow:
            0 2px 6px rgba(0,0,0,0.2),
            0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
        }
        .hero-cta::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 50%, transparent 100%);
          transition: left 0.5s ease;
        }
        .hero-cta:hover {
          background: linear-gradient(135deg, color-mix(in srgb, var(--vibe-color-primary) 90%, white) 0%, var(--vibe-color-primary) 100%);
          box-shadow:
            0 4px 12px rgba(0,0,0,0.2),
            0 8px 24px color-mix(in srgb, var(--vibe-color-primary) 40%, transparent);
          transform: translateY(-2px);
        }
        .hero-cta:hover::before {
          left: 100%;
        }
        .hero-cta:active {
          transform: translateY(0);
          box-shadow: 0 2px 6px rgba(0,0,0,0.2);
        }
        .hero-cta-secondary {
          background: rgba(255,255,255,0.08);
          border: 1px solid rgba(255,255,255,0.5);
          border-radius: 2px;
          backdrop-filter: blur(6px);
        }
        .hero-cta-secondary:hover {
          background: rgba(255,255,255,0.18);
          border-color: rgba(255,255,255,0.85);
          transform: translateY(-2px);
        }
        .hero-cta-secondary:active {
          transform: translateY(0);
        }
        .hero-accordion-trigger:hover {
          text-shadow: 0 0 8px rgba(255,255,255,0.3);
        }
        .hero-accordion-content {
          transition: height 0.3s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .hero-accordion-content[data-state='open'] {
          animation: heroAccordionOpen 0.3s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .hero-accordion-content[data-state='closed'] {
          animation: heroAccordionClose 0.2s cubic-bezier(0.22, 1, 0.36, 1);
        }
        @keyframes heroAccordionOpen {
          from { opacity: 0; transform: translateY(-8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes heroAccordionClose {
          from { opacity: 1; transform: translateY(0); }
          to   { opacity: 0; transform: translateY(-8px); }
        }
        .hero-accordion-body {
          background: rgba(0,0,0,0.35);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(255,255,255,0.1);
        }
        .hero-learn-more-content p {
          margin-bottom: 0.5em;
        }
        .hero-learn-more-content p:last-child {
          margin-bottom: 0;
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-entrance { animation: none; opacity: 1; transform: none; }
          .hero-accordion-content[data-state='open'],
          .hero-accordion-content[data-state='closed'] { animation: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Hero',
  displayName: 'Hero Section',
  tokens: {
    'hero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue:
        '<p>Welcome to <span data-type="field-token" data-field="property_name"></span></p>',
    },
    'hero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>Apartments for Rent</p>',
    },
    'hero.romance_blurb': {
      label: 'Home Romance Blurb',
      type: 'richtext',
      defaultValue:
        '<p>Find your perfect home in a community built for modern life. Enjoy upscale finishes, resort-style amenities, and a vibrant neighborhood right outside your door. Come see why residents love calling this place home.</p>',
      generator: {
        prompt:
          'Write a romance blurb for this property\'s home page that captures the feeling of living here.',
        context:
          'Sits directly below the subtitle in the hero section; sets the emotional tone before the CTAs.',
        length: '3 sentences',
      },
    },
    'hero.learn_more': {
      label: 'Learn More Content',
      type: 'richtext',
      defaultValue:
        '<p>Discover modern living at <span data-type="field-token" data-field="property_name"></span>. Our community offers thoughtfully designed floor plans, premium amenities, and an unbeatable location. Schedule a tour today to see your future home.</p>',
      generator: {
        prompt:
          'Write a concise overview of this property: what makes it stand out, key amenities, unit types, and location highlights.',
        context:
          'Shown in a collapsible "Learn More" accordion in the hero section — the first substantial description a visitor reads. Should inform without repeating the romance blurb directly above.',
        length: '2–4 sentences',
      },
    },
  },
  slotGroups: {
    'hero.bg_image': featuredImageDef,
    'hero.cta_link': ctaLinkDef,
    'hero.gallery_cta': galleryCtaDef,
    'hero.amenities_cta': amenitiesCtaDef,
    'hero.pet_policy_cta': petPolicyCtaDef,
    'hero.confetti_cta': confettiCtaDef,
  },
};

(Hero as any).contentSchema = contentSchema;

export default Hero;
